import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const RegistrationForm = ({ onSubmit, isLoading: externalLoading = false }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postalCode: '',
    password: '',
    confirmPassword: '',
    homeType: '',
    sustainabilityInterests: [],
    agreeToTerms: false,
    agreeToMarketing: false
  });

  const [errors, setErrors] = useState({});
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const homeTypeOptions = [
    { value: '', label: 'Selecteer woningtype' },
    { value: 'apartment', label: 'Appartement' },
    { value: 'terraced', label: 'Rijtjeshuis' },
    { value: 'semi-detached', label: 'Twee-onder-een-kap' },
    { value: 'detached', label: 'Vrijstaand huis' },
    { value: 'other', label: 'Anders' }
  ];

  const sustainabilityOptions = [
    { value: 'solar', label: 'Zonnepanelen' },
    { value: 'heatpump', label: 'Warmtepomp' },
    { value: 'battery', label: 'Thuisbatterij' },
    { value: 'insulation', label: 'Isolatie' },
    { value: 'water', label: 'Waterontharder' },
    { value: 'ventilation', label: 'Ventilatie' }
  ];

  const validatePostalCode = (code) => {
    const dutchPostalRegex = /^[1-9][0-9]{3}\s?[A-Z]{2}$/i;
    return dutchPostalRegex?.test(code);
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (/[A-Z]/?.test(password)) strength += 25;
    if (/[0-9]/?.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/?.test(password)) strength += 25;
    return strength;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Password strength calculation
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'Voornaam is verplicht';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Achternaam is verplicht';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Telefoonnummer is verplicht';
    } else if (!/^(\+31|0)[0-9]{9}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = 'Ongeldig Nederlands telefoonnummer';
    }

    if (!formData?.postalCode?.trim()) {
      newErrors.postalCode = 'Postcode is verplicht';
    } else if (!validatePostalCode(formData?.postalCode)) {
      newErrors.postalCode = 'Ongeldige Nederlandse postcode';
    }

    if (!formData?.password) {
      newErrors.password = 'Wachtwoord is verplicht';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Wachtwoord moet minimaal 8 karakters bevatten';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Wachtwoorden komen niet overeen';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'U moet akkoord gaan met de voorwaarden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Check if signUp function exists
      if (!signUp) {
        throw new Error('Authenticatie service niet beschikbaar. Ververs de pagina en probeer opnieuw.');
      }

      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        {
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          fullName: `${formData?.firstName} ${formData?.lastName}`,
          phone: formData?.phone,
          postalCode: formData?.postalCode,
          userType: 'customer'
        }
      );

      if (error) {
        console.error('Registratie fout:', error);
        const errorMessage = error?.message || 'Registratie mislukt. Probeer het opnieuw.';
        setErrors({ submit: errorMessage });
      } else {
        console.log('Registratie succesvol:', data);
        // Call parent onSubmit if provided for success handling
        if (onSubmit) {
          onSubmit(formData);
        } else {
          // Show success message
          alert('Account succesvol aangemaakt! Controleer uw e-mail voor verificatie.');
          // Navigate to success page or login
          navigate('/customer-login');
        }
      }
    } catch (error) {
      console.error('Onverwachte fout:', error);
      const errorMessage = error?.message || 'Er is een onverwachte fout opgetreden. Probeer het opnieuw.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Zwak';
    if (passwordStrength < 50) return 'Matig';
    if (passwordStrength < 75) return 'Goed';
    return 'Sterk';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors?.submit && (
        <div className="bg-error/10 border border-error/20 rounded-md p-3">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
            <p className="text-sm font-body text-error">
              {errors?.submit}
            </p>
          </div>
        </div>
      )}

      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Persoonlijke gegevens
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Voornaam"
            type="text"
            placeholder="Uw voornaam"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          
          <Input
            label="Achternaam"
            type="text"
            placeholder="Uw achternaam"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <Input
          label="E-mailadres"
          type="email"
          placeholder="uw.email@voorbeeld.nl"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Telefoonnummer"
            type="tel"
            placeholder="06 12345678"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />
          
          <Input
            label="Postcode"
            type="text"
            placeholder="1234 AB"
            value={formData?.postalCode}
            onChange={(e) => handleInputChange('postalCode', e?.target?.value?.toUpperCase())}
            error={errors?.postalCode}
            required
          />
        </div>
      </div>
      {/* Password Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Wachtwoord instellen
        </h3>
        
        <Input
          label="Wachtwoord"
          type="password"
          placeholder="Minimaal 8 karakters"
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          error={errors?.password}
          required
        />

        {/* Password Strength Indicator */}
        {formData?.password && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-body text-muted-foreground">
                Wachtwoordsterkte:
              </span>
              <span className={`text-sm font-caption font-medium ${
                passwordStrength < 50 ? 'text-error' : 'text-success'
              }`}>
                {getPasswordStrengthText()}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                style={{ width: `${passwordStrength}%` }}
              />
            </div>
          </div>
        )}

        <Input
          label="Bevestig wachtwoord"
          type="password"
          placeholder="Herhaal uw wachtwoord"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
      </div>
      {/* Optional Fields Toggle */}
      <div className="border-t border-border pt-6">
        <button
          type="button"
          onClick={() => setShowOptionalFields(!showOptionalFields)}
          className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-micro"
        >
          <Icon 
            name={showOptionalFields ? 'ChevronUp' : 'ChevronDown'} 
            size={16} 
          />
          <span className="text-sm font-body">
            {showOptionalFields ? 'Verberg' : 'Toon'} optionele velden
          </span>
        </button>

        {showOptionalFields && (
          <div className="mt-4 space-y-4">
            <Select
              label="Woningtype (optioneel)"
              options={homeTypeOptions}
              value={formData?.homeType}
              onChange={(value) => handleInputChange('homeType', value)}
              placeholder="Selecteer uw woningtype"
            />

            <Select
              label="Interesse in duurzaamheidsoplossingen (optioneel)"
              options={sustainabilityOptions}
              value={formData?.sustainabilityInterests}
              onChange={(value) => handleInputChange('sustainabilityInterests', value)}
              multiple
              searchable
              placeholder="Selecteer uw interesses"
              description="Kies de oplossingen waarin u geïnteresseerd bent"
            />
          </div>
        )}
      </div>
      {/* Terms and Conditions */}
      <div className="space-y-4">
        <Checkbox
          label="Ik ga akkoord met de algemene voorwaarden en het privacybeleid"
          checked={formData?.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
          error={errors?.agreeToTerms}
          required
        />

        <Checkbox
          label="Ik wil graag updates ontvangen over nieuwe providers en aanbiedingen"
          checked={formData?.agreeToMarketing}
          onChange={(e) => handleInputChange('agreeToMarketing', e?.target?.checked)}
          description="U kunt zich altijd uitschrijven"
        />
      </div>
      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading || externalLoading}
        iconName="UserPlus"
        iconPosition="left"
      >
        Account aanmaken
      </Button>
    </form>
  );
};

export default RegistrationForm;